// frontend/src/pages/DeviceDetail.js
import React from 'react';
import { useParams } from 'react-router-dom';
import { PlotDetailProvider, usePlotDetailContext } from '../context/PlotDetailContext';
import { useDeviceProvisioning } from '../hooks/useDeviceProvisioning';
import PlotHeader from '../components/plot/PlotHeader';
import DeviceList from '../components/plot/DeviceList';
import DisplayControls from '../components/plot/DisplayControls';
import { MetricCategoryPanel } from '../components/plot/MetricCharts';

const CATEGORY_TITLES = {
  weather: 'Weather Conditions (Raw Telemetry)',
  realtime: 'Realtime Conditions (Raw Telemetry)'
};

function DeviceDetailContent() {
  const { farmId, plotId } = useParams();
  const {
    plot,
    plotData,
    loading,
    error,
    chartData,
    categoryGroups = {},
    visibleDeviceIds,
    visibleDevices,
    activeMetrics,
    toggleDevice,
    toggleMetric,
    fetchPlotDetails
  } = usePlotDetailContext() || {};

  // Device Provisioning modal hook with automatic refresh
  const { openAddDeviceModal, DeviceProvisioningModal } = useDeviceProvisioning(async () => {
    if (fetchPlotDetails) {
      await fetchPlotDetails();
    }
  });

  const activePlot = plotData || plot;

  if (loading) {
    return <div style={{ padding: '32px', textAlign: 'center', color: '#64748b' }}>Loading raw device telemetry...</div>;
  }

  if (error || !activePlot) {
    return <div style={{ padding: '32px', color: '#dc2626' }}>{error || 'Device or plot data unavailable.'}</div>;
  }

  // REQUIREMENT 2: Exclude accumulation/phenology categories (derived values)
  const filteredCategoryGroups = { ...categoryGroups };
  delete filteredCategoryGroups.accumulation;
  delete filteredCategoryGroups.crop;

  const allAvailableMetrics = Object.values(filteredCategoryGroups)
    .flat()
    .map((m) => m.type);

  const effectiveActiveMetrics = activeMetrics && activeMetrics.length > 0
    ? activeMetrics.filter((m) => allAvailableMetrics.includes(m))
    : allAvailableMetrics;

  return (
    <div className="device-detail-page" style={{ padding: '24px', background: '#f8fafc', minHeight: '100vh', fontFamily: 'sans-serif' }}>

      {/* 1. Plot & Device Header */}
      <div style={{ background: '#ffffff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
        <PlotHeader metadata={activePlot.metadata || activePlot} plotId={plotId} farmId={farmId} />
        <DeviceList
          devices={activePlot.devices || activePlot}
          visibleDeviceIds={visibleDeviceIds}
          onToggleDevice={toggleDevice}
          onAddDevice={(targetPlot) => openAddDeviceModal(targetPlot || activePlot)}
          onOpenModal={() => openAddDeviceModal(activePlot)}
          isDeviceDetailPage={true} // Hides "Go to Site Devices" link
        />
      </div>

      {/* 2. Display Controls (Forecast disabled/hidden for raw data) */}
      <DisplayControls
        categoryGroups={filteredCategoryGroups}
        activeMetrics={effectiveActiveMetrics}
        onToggleMetric={toggleMetric}
        forecastVisible={false}
        forecastDisabled={true}
      />

      {/* 3. Raw Data Metric Category Charts */}
      {Object.keys(CATEGORY_TITLES).map((catKey) => {
        const catMetrics = (filteredCategoryGroups[catKey] || []).filter((m) => effectiveActiveMetrics.includes(m.type));
        if (!catMetrics || catMetrics.length === 0) return null;

        return (
          <MetricCategoryPanel
            key={catKey}
            title={CATEGORY_TITLES[catKey]}
            metrics={catMetrics}
            chartData={chartData}
            visibleDevices={visibleDevices}
          />
        );
      })}

      {/* 4. Hardware Provisioning Modal */}
      <DeviceProvisioningModal />
    </div>
  );
}

export default function DeviceDetail() {
  const { farmId, plotId } = useParams();

  return (
    <PlotDetailProvider farmId={farmId} plotId={plotId}>
      <DeviceDetailContent />
    </PlotDetailProvider>
  );
}
